import { useCallback, useState } from "react";

import type {
	PriceRankItem,
	RoomPriceRank_RoomPriceRankPageQuery,
	RoomPriceRankDailyStatus_RoomPriceRankPageQuery,
} from "@/lib/gql/__generated__/graphql";

import { useRoomPriceRankDailyStatusModel } from "../../_shared/daily-stasus/use-case/use-room-price-rank-daily-status-model";
import { useRoomPriceRankModel } from "../../_shared/price-rank/use-case/use-room-price-rank-model";
import { useUpdateRoomPriceSetting } from "../use-update-room-price-setting/use-update-room-price-setting";

// eslint-disable-next-line import/no-restricted-paths
import type { ApolloQueryResult } from "@apollo/client";

type Props = {
	hotelID: string;
	roomPriceRank: RoomPriceRank_RoomPriceRankPageQuery;
	roomPriceRankDailyStatus: RoomPriceRankDailyStatus_RoomPriceRankPageQuery;
	onRefetch: (props: {
		hotelID: string;
		startDt: string;
		endDt: string;
	}) => Promise<ApolloQueryResult<RoomPriceRankDailyStatus_RoomPriceRankPageQuery | undefined>>;
};

export const useInit = ({ hotelID, roomPriceRank: _roomPriceRank, roomPriceRankDailyStatus, onRefetch }: Props) => {
	const [selectedPriceRank, setSelectedPriceRank] = useState<PriceRankItem | undefined>(
		_roomPriceRank.roomPriceRank.ranks.length ? _roomPriceRank.roomPriceRank.ranks.at(0) : undefined,
	);
	const onSetSelectedPriceRank = useCallback((input: PriceRankItem) => {
		setSelectedPriceRank(input);
	}, []);

	const {
		roomPriceRank,
		reset: resetRoomPriceRankViewModel,
		onSetRoomPriceRank,
		fetchNewPriceRankItem,
	} = useRoomPriceRankModel({
		serverModel: _roomPriceRank,
	});

	const {
		dailyStatus,
		onSetDailyStatus,
		reset: resetDailyStatusViewModel,
		refetchRoomPriceRankDailyStatus,
	} = useRoomPriceRankDailyStatusModel({
		hotelID,
		roomPriceRankDailyStatus,
		onRefetch,
	});

	const isDirty = roomPriceRank.isDirty || dailyStatus.isDirty;
	const hasError = roomPriceRank.hasError || dailyStatus.hasError;
	const isValid = isDirty && !hasError;

	const { onUpdate: onUpdateRoomPriceSetting } = useUpdateRoomPriceSetting();

	const onUpdate = useCallback(async () => {
		if (!isValid) return;

		const successCallback = () => {
			resetRoomPriceRankViewModel();
			resetDailyStatusViewModel();
		};

		await onUpdateRoomPriceSetting({
			roomPriceRankViewModel: roomPriceRank,
			roomPriceRankDailyStatusViewModel: dailyStatus,
			successCallback,
		});
	}, [
		dailyStatus,
		isValid,
		onUpdateRoomPriceSetting,
		resetDailyStatusViewModel,
		resetRoomPriceRankViewModel,
		roomPriceRank,
	]);

	return {
		onUpdate,
		isDirty,
		hasError,
		isValid,

		roomPriceRank,
		onSetRoomPriceRank,
		fetchNewPriceRankItem,

		dailyStatus,
		onSetDailyStatus,
		refetchRoomPriceRankDailyStatus,

		selectedPriceRank,
		onSetSelectedPriceRank,

		siteControllerSetting: _roomPriceRank.siteControllerSetting,
	};
};
